import {BaseCommand} from './base.command';
import {EdgeControlPointSnapshot} from '../interfaces/command.interface';
import {CytoscapeService} from '../../cy-services/cytoscape.service';
import {NodeService} from '../../../api/services/node.service';
import {firstValueFrom, of} from 'rxjs';
import {catchError, defaultIfEmpty} from 'rxjs/operators';
import {UndoLabelMetadata} from '../undo-label.utils';

/**
 * EdgeAnchorCommand handles changes to edge anchor/control points.
 * This is used when users directly manipulate bend/control points on edges.
 */
export class EdgeAnchorCommand extends BaseCommand<EdgeControlPointSnapshot> {
  constructor(
    private cyService: CytoscapeService,
    private nodeService: NodeService,
    before: EdgeControlPointSnapshot,
    after: EdgeControlPointSnapshot
  ) {
    const labelMetadata: UndoLabelMetadata = {
      object: 'Link',
      verb: 'Change',
      part: 'Curvature'
    };

    super(
      'edge-anchor',
      'Link Curvature Change',
      {before, after},
      labelMetadata
    );
  }

  async execute(): Promise<void> {
    const cy = this.cyService.cy;
    if (!cy) return;

    const after = this.snapshot.after;
    const edge = cy.$(`#${after.edgeId}`);
    if (edge.length === 0) return;

    this.applyEdgeControlPoints(edge, after);
    this.reinitializeEdgeAnchors(cy, edge);
    await this.updateEdgeOnBackend(after);
  }

  async undo(): Promise<void> {
    const cy = this.cyService.cy;
    if (!cy) return;

    const before = this.snapshot.before;
    const edge = cy.$(`#${before.edgeId}`);
    if (edge.length === 0) return;

    this.applyEdgeControlPoints(edge, before);
    this.reinitializeEdgeAnchors(cy, edge);
    await this.updateEdgeOnBackend(before);
  }

  private applyEdgeControlPoints(edge: any, snapshot: EdgeControlPointSnapshot): void {
    if (snapshot.bendPointPositions !== undefined) {
      edge.data('bendPointPositions', snapshot.bendPointPositions);
    }
    if (snapshot.controlPointPositions !== undefined) {
      edge.data('controlPointPositions', snapshot.controlPointPositions);
    }
    if (snapshot.cyedgebendeditingDistances !== undefined) {
      edge.data('cyedgebendeditingDistances', snapshot.cyedgebendeditingDistances);
    }
    if (snapshot.cyedgebendeditingWeights !== undefined) {
      edge.data('cyedgebendeditingWeights', snapshot.cyedgebendeditingWeights);
    }
    if (snapshot.cyedgecontroleditingDistances !== undefined) {
      edge.data('cyedgecontroleditingDistances', snapshot.cyedgecontroleditingDistances);
    }
    if (snapshot.cyedgecontroleditingWeights !== undefined) {
      edge.data('cyedgecontroleditingWeights', snapshot.cyedgecontroleditingWeights);
    }
  }

  private reinitializeEdgeAnchors(cy: any, edge: any): void {
    try {
      const edgeEditingInstance = cy.edgeEditing?.();
      if (edgeEditingInstance?.initAnchorPoints) {
        edgeEditingInstance.initAnchorPoints([edge]);
      }
    } catch (err) {
      console.warn('EdgeAnchorCommand: Failed to reinitialize edge anchors', err);
    }
  }

  private async updateEdgeOnBackend(snapshot: EdgeControlPointSnapshot): Promise<void> {
    const payload: any = {
      id: snapshot.edgeId,
      bendPointPositions: snapshot.bendPointPositions,
      controlPointPositions: snapshot.controlPointPositions,
      cyedgebendeditingDistances: snapshot.cyedgebendeditingDistances,
      cyedgebendeditingWeights: snapshot.cyedgebendeditingWeights,
      cyedgecontroleditingDistances: snapshot.cyedgecontroleditingDistances,
      cyedgecontroleditingWeights: snapshot.cyedgecontroleditingWeights
    };

    try {
      await firstValueFrom(
        this.nodeService.updateLink(payload).pipe(
          defaultIfEmpty(null),
          catchError(err => {
            console.warn('EdgeAnchorCommand: Failed to update edge control points', snapshot.edgeId, err);
            return of(null);
          })
        )
      );
    } catch {
      // Ignore errors
    }
  }
}
